import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { PayslipApiFactory } from '../api/payslipApi';
import type { PayslipDetail } from '../payslipTypes';

interface UseGetPayslipResult {
  data: PayslipDetail | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Fetches one payslip's detail (frozen header + ordered rubrique lines). On
 * failure the hook surfaces the error and returns no data — the caller shows an
 * error message (no mock/demo fallback: sample data must never be mistaken for a
 * real payslip). Passing a null `payslipId` fetches nothing (detail closed).
 */
export function useGetPayslip(employeeId: string, payslipId: string | null): UseGetPayslipResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => PayslipApiFactory(client), [client]);

  const [data, setData] = useState<PayslipDetail | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!employeeId || !payslipId) {
      setData(null);
      setError(null);
      return;
    }

    const ctrl = new AbortController();

    api
      .getById(employeeId, payslipId, { signal: ctrl.signal })
      .then(detail => {
        setError(null);
        setData(detail);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setData(null);
      });

    return () => ctrl.abort();
  }, [api, employeeId, payslipId]);

  return { data, loading, error };
}
